import { ChangeDetectionStrategy, ChangeDetectorRef, Component, EventEmitter, Input, OnDestroy, OnInit, Output, inject } from '@angular/core';
import { Router } from '@angular/router';

import { guid } from '@firestitch/common';
import { FsMessage } from '@firestitch/message';

import { Observable, Subject, forkJoin, of, zip } from 'rxjs';
import { map, switchMap, takeUntil, tap } from 'rxjs/operators';

import { AclRoleData } from '@app/admin';
import { AclRoleReferences } from '@common/consts';

import { AuthService, SessionService } from '@app/core/services';
import { EnvironmentData, InviteData, OrganizationData } from '@common/data';
import { AclRoleReference } from '@common/enums';
import { Account, AclRole, Environment, Project } from '@common/interfaces';


import { FormsModule } from '@angular/forms';
import { MatButton } from '@angular/material/button';
import { MatCardActions, MatCardContent } from '@angular/material/card';
import { MatOption } from '@angular/material/core';
import { MatFormField, MatLabel } from '@angular/material/form-field';
import { MatInput } from '@angular/material/input';
import { MatSelect } from '@angular/material/select';
import { FsFormModule } from '@firestitch/form';
import { FsSkeletonModule } from '@firestitch/skeleton';
import { EnvironmentCreate } from '../../interfaces';


@Component({
    selector: 'app-invite',
    templateUrl: './invite.component.html',
    styleUrls: ['./invite.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: true,
    imports: [
    FsSkeletonModule,
    FormsModule,
    FsFormModule,
    MatCardContent,
    MatFormField,
    MatLabel,
    MatInput,
    MatSelect,
    MatOption,
    MatCardActions,
    MatButton
],
})
export class InviteComponent implements OnInit, OnDestroy {
  private _cd = inject(ChangeDetectorRef);
  private _router = inject(Router);
  private _environmentData = inject(EnvironmentData);
  private _organizationData = inject(OrganizationData);
  private _authService = inject(AuthService);
  private _sessionService = inject(SessionService);
  private _inviteData = inject(InviteData);
  private _fsMessage = inject(FsMessage);
  private _aclRoleData = inject(AclRoleData);


  @Input() public environmentCreate: EnvironmentCreate = null;

  @Output() public completed = new EventEmitter();

  public aclRoleReferences = AclRoleReferences;

  public account: Account = null;

  private _destroy$ = new Subject();

  public ngOnInit(): void {
    this._sessionService.account$()
      .pipe(
        takeUntil(this._destroy$),
      )
      .subscribe((response) => this.account = response);

    this.memberAdd(3);
  }

  public ngOnDestroy(): void {
    this._destroy$.next(null);
    this._destroy$.complete();
  }

  public focus(index: number): void {
    if (index === (this.environmentCreate.members.length - 1)) {
      this.memberAdd();
    }
  }

  public memberAdd(count: number = 1): void {
    for (let i = 0; i < count; i++) {
      this.environmentCreate.members.push({
        email: null,
        aclRoleReference: AclRoleReference.OrganizationAdmin,
        guid: guid(),
      });
    }

    this._cd.detectChanges();
  }

  public save = () => {
    return this._organizationData.post({ name: this.environmentCreate.workspaceName })
      .pipe(
        switchMap((environment: Environment) => {
          return zip(
            this._authService.reloadPermissions(),
            this._environmentData.switch(environment.id),
          )
            .pipe(
              map(() => {
                return environment;
              }),
              takeUntil(this._destroy$),
            );
        }),
        switchMap((environment: Environment) => this._saveInvites(environment)),
      )
      .pipe(
        tap(() => {
          this._fsMessage.success('Your organization is ready');
          this._router.navigate(['/organization', this._sessionService.environmentId()]);
          this.completed.emit();
        }),
        takeUntil(this._destroy$),
      );
  };

  private _saveInvites(environment: Environment): Observable<Project> {
    return this._aclRoleData.gets({
      environmentId: environment.id,
      reference: AclRoleReferences.map((item) => {
        return item.value;
      }).join(','),
    })
      .pipe(
        switchMap((aclRoles) => {
          const queries$ = [];
          this.environmentCreate.members
            .filter((item) => item.email && item.aclRoleReference)
            .forEach((member) => {
              const selectedRole = aclRoles
                .find((role: AclRole) => {
                  return role.reference === member.aclRoleReference;
                });

              queries$.push(this._inviteData.post({
                email: member.email,
                environmentId: environment.id,
                level: selectedRole.level,
                objects: [
                  {
                    aclRoleIds: [selectedRole.id],
                    objectId: environment.id,
                  },
                ],
              }));
            });

          if (!queries$.length) {
            queries$.push(of(true));
          }

          return forkJoin(...queries$)
            .pipe(
              takeUntil(this._destroy$),
            );
        }),
      );
  }

}
